const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (user && user.token) {
      return { "Authorization": `Bearer ${user.token}` };
    }
  } catch (e) { }
  return {};
};

export const api = {
  // Auth
  register: async (data) => {
    const res = await fetch(`${API_URL}/api/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Registration failed");
    }
    return res.json();
  },

  login: async (data) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Login failed");
    }
    return res.json();
  },

  logout: async () => {
    await fetch(`${API_URL}/api/auth/logout`, {
      method: "POST",
      headers: { ...getAuthHeader() },
      credentials: "include"
    });
    localStorage.removeItem("user");
  },

  forgotPassword: async (email) => {
    const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || "Could not send reset email");
    return data;
  },

  resetPassword: async (token, password) => {
    const res = await fetch(`${API_URL}/api/auth/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ token, password })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || "Could not reset password");
    return data;
  },

  deleteAccount: async ({ password, confirm }) => {
    const res = await fetch(`${API_URL}/api/auth/account`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json", ...getAuthHeader() },
      credentials: "include",
      body: JSON.stringify({ password, confirm })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || "Could not delete account");
    localStorage.removeItem("user");
    return data;
  },

  getMe: async () => {
    const res = await fetch(`${API_URL}/api/auth/me`, {
      headers: { ...getAuthHeader() },
      credentials: "include"
    });
    if (!res.ok) throw new Error("Not authenticated");
    return res.json();
  },

  // Profile
  getProfile: async () => {
    const res = await fetch(`${API_URL}/api/profile`, {
      headers: { ...getAuthHeader() },
      credentials: "include"
    });
    if (!res.ok) throw new Error("Failed to fetch profile");
    return res.json();
  },

  updateProfile: async (data) => {
    const res = await fetch(`${API_URL}/api/profile`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...getAuthHeader() },
      credentials: "include",
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error("Failed to update profile");
    return res.json();
  },

  // Countries & Visas
  getCountries: async (region = null) => {
    const url = region ? `${API_URL}/api/countries?region=${region}` : `${API_URL}/api/countries`;
    const res = await fetch(url);
    if (!res.ok) throw new Error("Failed to fetch countries");
    return res.json();
  },

  getCountry: async (countryId) => {
    const res = await fetch(`${API_URL}/api/countries/${countryId}`);
    if (!res.ok) throw new Error("Country not found");
    return res.json();
  },

  getVisa: async (countryId, visaType) => {
    const res = await fetch(`${API_URL}/api/visas/${countryId}/${visaType}`);
    if (!res.ok) throw new Error("Visa not found");
    return res.json();
  },

  // Eligibility
  checkEligibility: async () => {
    const res = await fetch(`${API_URL}/api/eligibility`, {
      headers: { ...getAuthHeader() },
      credentials: "include"
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to check eligibility");
    }
    return res.json();
  },

  // User Countries
  getUserCountries: async () => {
    const res = await fetch(`${API_URL}/api/user/countries`, {
      headers: { ...getAuthHeader() },
      credentials: "include"
    });
    if (!res.ok) throw new Error("Failed to fetch saved countries");
    return res.json();
  },

  addUserCountry: async (data) => {
    const res = await fetch(`${API_URL}/api/user/countries`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeader() },
      credentials: "include",
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to add country");
    }
    return res.json();
  },

  updateUserCountry: async (ucId, data) => {
    const res = await fetch(`${API_URL}/api/user/countries/${ucId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...getAuthHeader() },
      credentials: "include",
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error("Failed to update");
    return res.json();
  },

  deleteUserCountry: async (ucId) => {
    const res = await fetch(`${API_URL}/api/user/countries/${ucId}`, {
      method: "DELETE",
      headers: { ...getAuthHeader() },
      credentials: "include"
    });
    if (!res.ok) throw new Error("Failed to delete");
    return res.json();
  },

  // Documents
  getDocuments: async () => {
    const res = await fetch(`${API_URL}/api/documents`, {
      headers: { ...getAuthHeader() },
      credentials: "include"
    });
    if (!res.ok) throw new Error("Failed to fetch documents");
    return res.json();
  },

  addDocument: async (data) => {
    const res = await fetch(`${API_URL}/api/documents`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeader() },
      credentials: "include",
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error("Failed to add document");
    return res.json();
  },

  deleteDocument: async (docId) => {
    const res = await fetch(`${API_URL}/api/documents/${docId}`, {
      method: "DELETE",
      headers: { ...getAuthHeader() },
      credentials: "include"
    });
    if (!res.ok) throw new Error("Failed to delete document");
    return res.json();
  },

  // Payments
  createCheckout: async (packageId = "standard") => {
    const body = JSON.stringify({
      package_id: packageId,
      origin_url: window.location.origin
    });
    const res = await fetch(`${API_URL}/api/payments/checkout`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeader() },
      credentials: "include",
      body
    });
    if (!res.ok) {
      let detail = "Failed to create checkout";
      try {
        const err = await res.json();
        detail = err.detail || detail;
      } catch { }
      throw new Error(detail);
    }
    return res.json();
  },

  // Stripe Customer Portal: cancel, resume, update card, see invoices.
  createPortalSession: async () => {
    const res = await fetch(`${API_URL}/api/payments/portal`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeader() },
      credentials: "include",
      body: JSON.stringify({ return_url: `${window.location.origin}/profile` })
    });
    if (!res.ok) {
      let detail = "Could not open billing portal";
      try {
        const err = await res.json();
        detail = err.detail || detail;
      } catch { }
      throw new Error(detail);
    }
    return res.json();
  },

  getPaymentStatus: async (sessionId) => {
    const res = await fetch(`${API_URL}/api/payments/status/${sessionId}`, {
      headers: { ...getAuthHeader() },
      credentials: "include"
    });
    if (!res.ok) throw new Error("Failed to get payment status");
    return res.json();
  },

  // Export PDF
  exportCountryPdf: async (countryId, visaType) => {
    const res = await fetch(`${API_URL}/api/export/${countryId}/${visaType}/pdf`, {
      headers: { ...getAuthHeader() },
      credentials: "include"
    });
    if (!res.ok) throw new Error("Failed to export PDF");
    return res;
  },

  // AI Draft
  generateDraft: async (data) => {
    const res = await fetch(`${API_URL}/api/ai/generate-draft`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeader() },
      credentials: "include",
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to generate draft");
    }
    return res.json();
  }
};

export default api;

// ============ TRAVEL / SCHENGEN ============
export const travelApi = {
  getLogs: async () => {
    const res = await fetch(`${API_URL}/api/travel/logs`, {
      headers: { ...getAuthHeader() },
      credentials: "include"
    });
    if (!res.ok) throw new Error("Failed to fetch travel logs");
    return res.json();
  },

  getStatus: async () => {
    const res = await fetch(`${API_URL}/api/travel/status`, {
      headers: { ...getAuthHeader() },
      credentials: "include"
    });
    if (!res.ok) throw new Error("Failed to fetch Schengen status");
    return res.json();
  },

  addLog: async (data) => {
    const res = await fetch(`${API_URL}/api/travel/logs`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeader() },
      credentials: "include",
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || "Failed to add travel log");
    }
    return res.json();
  },

  deleteLog: async (logId) => {
    const res = await fetch(`${API_URL}/api/travel/logs/${logId}`, {
      method: "DELETE",
      headers: { ...getAuthHeader() },
      credentials: "include"
    });
    if (!res.ok) throw new Error("Failed to delete travel log");
    return res.json();
  }
};
